import React, { useEffect, useState } from "react";
import AppShell from "@/components/AppShell";
import { useLocale } from "@/contexts/LocaleContext";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { BellRing, CheckCircle2, Inbox, Archive } from "lucide-react";

export default function Notifications() {
  const { formatDateTime } = useLocale();
  const [tab, setTab] = useState("unread"); // unread | read | archived
  const [items, setItems] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/notifications/in-app", { params: { status: tab, limit: 100 } });
      setItems(data.items || []);
      setUnreadCount(data.unread_count ?? 0);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [tab]);

  const markRead = async (id) => {
    try { await api.post(`/notifications/in-app/${id}/mark-read`); load(); }
    catch (e) { toast.error(formatApiError(e)); }
  };
  const markAll = async () => {
    try { await api.post("/notifications/in-app/mark-all-read"); load(); toast.success("All marked read"); }
    catch (e) { toast.error(formatApiError(e)); }
  };

  return (
    <AppShell>
      <div className="flex items-start justify-between gap-4 flex-wrap mb-6">
        <div>
          <div className="eb-overline mb-2">Module 6 · Host notifications</div>
          <h1 className="text-3xl font-semibold flex items-center gap-3" data-testid="notif-heading">
            <BellRing className="w-7 h-7 text-orange-500" />
            Notifications
            {unreadCount > 0 && (
              <span className="text-sm bg-orange-100 text-orange-700 px-2 py-0.5 rounded-full font-bold" data-testid="notif-unread-count">
                {unreadCount} unread
              </span>
            )}
          </h1>
          <p className="text-sm text-navy-500 mt-1">In-app feed of your visitor arrivals (refreshes automatically every 30s).</p>
        </div>
        {unreadCount > 0 && (
          <button onClick={markAll} className="btn-accent text-xs" data-testid="notif-mark-all-page">
            <CheckCircle2 className="w-4 h-4" /> Mark all read
          </button>
        )}
      </div>

      <div className="flex gap-1 mb-5 border-b border-navy-100">
        {[
          { k: "unread", label: "Unread", icon: Inbox },
          { k: "read", label: "Read", icon: CheckCircle2 },
          { k: "archived", label: "Archived", icon: Archive },
        ].map((t) => (
          <button key={t.k} onClick={() => setTab(t.k)}
            className={`px-4 py-2 text-sm font-medium border-b-2 inline-flex items-center gap-2 ${tab === t.k ? "border-orange-500 text-orange-600" : "border-transparent text-navy-600"}`}
            data-testid={`notif-tab-${t.k}`}
          >
            <t.icon className="w-4 h-4" /> {t.label}
          </button>
        ))}
      </div>

      <div className="eb-card p-0 overflow-hidden" data-testid="notif-list">
        {loading ? (
          <p className="text-navy-500 py-10 text-center">Loading…</p>
        ) : items.length === 0 ? (
          <div className="py-16 text-center text-sm text-navy-500">No notifications here.</div>
        ) : (
          <ul className="divide-y divide-navy-100">
            {items.map((n) => {
              const p = n.payload || {};
              return (
                <li key={n.id} className="px-5 py-4 hover:bg-navy-50/40" data-testid={`notif-row-${n.id}`}>
                  <div className="flex items-start gap-4">
                    {p.photo_url ? (
                      <img src={p.photo_url} alt="" className="w-12 h-12 rounded-lg object-cover" />
                    ) : (
                      <div className="w-12 h-12 rounded-lg bg-navy-100 flex items-center justify-center text-navy-500 font-bold">
                        {(p.visitor_name || "?").slice(0, 1)}
                      </div>
                    )}
                    <div className="flex-1 min-w-0">
                      <div className="text-sm">
                        <strong className="text-navy-900">{p.visitor_name}</strong>
                        {p.visitor_company ? <span className="text-navy-500"> ({p.visitor_company})</span> : null}
                        {" "}signed in{p.site_name ? ` at ${p.site_name}` : ""}
                      </div>
                      {p.deviation_tag && (
                        <div className="text-[11px] text-orange-600 mt-1" data-testid="notif-deviation">{p.deviation_tag}</div>
                      )}
                      <div className="text-[10px] text-navy-400 mt-1">
                        {n.created_at ? formatDateTime(n.created_at) : ""}
                      </div>
                    </div>
                    {tab === "unread" && (
                      <button onClick={() => markRead(n.id)} className="btn-ghost text-xs" data-testid={`notif-mark-${n.id}`}>
                        Mark read
                      </button>
                    )}
                  </div>
                </li>
              );
            })}
          </ul>
        )}
      </div>
    </AppShell>
  );
}
